import React, {useEffect, useMemo, memo} from "react";
import {useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTag} from "@fortawesome/free-solid-svg-icons/faTag";
import {faChevronRight} from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import dayjs from "dayjs";

import {useCountdown} from "@/hooks/useCountdown/useCountdown.js";
import {formatNumber} from "@/helpers/formatNumber.js";
import {closeBootstrapModal} from "@/helpers/closeBootstrapModal.js";
import Badge from "@/components/Global/Badge/Badge";
import Box from "@/components/Global/Box/Box";
import Button from "@/components/Global/Button/Button";
import Divider from "@/components/Global/Divider/Divider";
import Icon from "@/components/Global/Icon/Icon";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import Timer from "@/components/Global/Timer/Timer";
import PreOrderModal from "./PreOrderModal/PreOrderModal";

import styles from "./TestOfferBanner.module.scss";
import {selectPhlebotomyOptions} from "@/features/appSettings/appSettingsSelectors";

export default memo(function TestOfferBanner({
  offerValidUntil = dayjs(),
  offerPercentage = 0,
  testPrice = 0,
  testName = "test",
  testIcon = "",
  collectionMethod = 0,
  testId = 0,
  className = "",
}) {
  const collectionMethods = useSelector(selectPhlebotomyOptions);

  const phlebotomyPrice = useMemo(
    () => parseFloat(collectionMethods.find(method => method.phlebotomyId == collectionMethod)?.phlebotomyPrice || 0),
    [collectionMethods, collectionMethod],
  );

  const timeDelta = useMemo(() => dayjs(offerValidUntil).diff(dayjs(), "second") || 0, [offerValidUntil]);
  const {daysRemaining, hoursRemaining, minutesRemaining, secondsRemaining, activateCountdown} =
    useCountdown(timeDelta);

  useEffect(() => {
    activateCountdown();

    return () => {
      closeBootstrapModal("preOrderModal");
    };
  }, []);

  const isOfferExpired = [daysRemaining, hoursRemaining, minutesRemaining, secondsRemaining].every(time => time == 0);

  const originalPrice = useMemo(
    () => formatNumber(parseFloat(testPrice) + phlebotomyPrice),
    [testPrice, phlebotomyPrice],
  );

  const testWithOffer = useMemo(
    () => formatNumber(parseFloat(testPrice) - (parseFloat(testPrice) * offerPercentage) / 100 + phlebotomyPrice),
    [testPrice, offerPercentage, phlebotomyPrice],
  );

  return (
    <RenderIf isTrue={isOfferExpired === false}>
      <Box variant="red-border" className={`p-3 ${className}`}>
        <header className="row mb-3">
          <div className="col-auto">
            <Badge
              variant="dark-pink"
              size="sm"
              className={`${styles.badge} d-flex align-items-center text-uppercase py-1 px-2`}>
              <FontAwesomeIcon icon={faTag} className="me-2 fa-fw" size="lg" /> Limited time offer
            </Badge>
          </div>
        </header>
        <div className={`row align-items-center gap-2 ${styles.content}`}>
          <div className={`${testIcon ? "col-7" : "col-12"}`}>
            <h3 className="text-balance">
              Save {offerPercentage}% on your next {testName}
            </h3>
          </div>
          <RenderIf isTrue={testIcon}>
            <div className="col">
              <Icon icon={testIcon} className={`mx-auto ${styles.testIcon}`} colours={["black"]} />
            </div>
          </RenderIf>
          <div className="col-12 pt-2 pb-3">
            Order your next test in advance and save {offerPercentage}% off the full price
          </div>
          <div className="col-12">
            <p className={`fw-semibold mb-2 ${styles.colorRed}`}>
              <small>Hurry! Offer ends in...</small>
            </p>
            <Timer
              className={`${styles.timer} py-3 px-2 mb-3`}
              faIconClassName={"px-0 px-md-3"}
              daysRemaining={daysRemaining}
              hoursRemaining={hoursRemaining}
              minutesRemaining={minutesRemaining}
              secondsRemaining={secondsRemaining}
            />
          </div>
          <Divider className={`${styles.divider}`} />
        </div>
        <footer className="row align-items-center justify-content-between mt-3">
          <div className="col-auto">
            <p className={styles.oldPrice}>
              <span className={`${testPrice <= 0 ? "d-none" : ""}`}>£</span>
              {originalPrice}
            </p>
            <p className={styles.newPrice}>
              <span className={`${testWithOffer <= 0 ? "d-none" : ""}`}>£</span>
              {testWithOffer <= 0 ? "FREE" : testWithOffer}
            </p>
            <RenderIf isTrue={collectionMethod == 2 || collectionMethod == 5}>
              <small className="d-block fst-italic fw-normal">{testName} + Phlebotomy</small>
            </RenderIf>
          </div>
          <div className="col-auto text-end">
            <RenderIf isTrue={!isOfferExpired}>
              <Button
                variant="link-default"
                className="d-flex align-items-center text-black px-0"
                data-bs-target={"#preOrderModal"}
                data-bs-toggle="modal">
                Start Pre-Order
                <FontAwesomeIcon icon={faChevronRight} className="fa-fw ms-1" />
              </Button>
            </RenderIf>
          </div>
        </footer>
      </Box>

      <PreOrderModal
        testPrice={testWithOffer}
        testId={testId}
        collectionMethod={collectionMethod}
        modalId="preOrderModal"
      />
    </RenderIf>
  );
});
